import {
  useCheckVerificationCodeMutation,
  useSendVerificationCodeMutation,
} from 'app/services/phoneVerification';
import { PhoneIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import styles from 'Components/Auth/TextInput/TextInput.module.css';
import ErrorMessageStyle from 'Components/Form/ErrorMessageStyle';
import Label from 'Components/Form/Label';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import useCreateModal from 'Components/Popups/useCreateModal';
import UserInfo from 'Components/Profile/UserInfo';
import { useEffect, useState } from 'react';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';

export default function PhoneNumberEditor({
  phoneNumber: userPhoneNumber,
  onPhoneNumberChange,
  onPhoneNumberVerify,
}: {
  phoneNumber?: string;
  onPhoneNumberVerify?: (v: boolean) => void;
  onPhoneNumberChange?: (p: string) => void;
}) {
  const [sendVerificationCode] = useSendVerificationCodeMutation();
  const [checkVerificationCode] = useCheckVerificationCodeMutation();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [code, setCode] = useState('');
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const VerifyCodeModal = useCreateModal();
  useEffect(() => {
    setIsValidPhoneNumber(isPossiblePhoneNumber(phoneNumber));
  }, [phoneNumber]);

  const sendVerificationCodeHandle = () => {
    if (!isValidPhoneNumber) return;
    sendVerificationCode({ phone_number: phoneNumber })
      .unwrap()
      .then((_) => {
        successToast('Verification Code Sent');
        VerifyCodeModal.open();
        VerifyCodeModal.lock();
      })
      .catch((_) => {
        errorToast('Incorrect phone number');
      });
  };

  const checkVerificationCodeHandle = () => {
    checkVerificationCode({ phone_number: phoneNumber, code })
      .unwrap()
      .then((_) => {
        successToast('Verification Code Accepted');
        onPhoneNumberVerify && onPhoneNumberVerify(true);
        VerifyCodeModal.forceClose();
      })
      .catch((_) => {
        errorToast('Invalid code');
      });
  };

  return (
    <>
      <div className="flex flex-col w-full">
        {!!userPhoneNumber && (
          <UserInfo
            userInfo={userPhoneNumber}
            label="Phone Number"
            icon={<PhoneIcon />}
          />
        )}
        {!userPhoneNumber && (
          <>
            <Label label="Phone Number" />
            <PhoneInput
              value={phoneNumber}
              onChange={(phone) => {
                const p = phone?.toString() || '';
                setPhoneNumber(p);
                onPhoneNumberChange && onPhoneNumberChange(p);
              }}
              defaultCountry="US"
              className="bg-gray-300 w-full h-12 px-2 rounded-xl border border-gray-100  text-sm focus-within:outline-none"
              placeholder="Enter phone number"
            />
            {!isValidPhoneNumber && phoneNumber && (
              <ErrorMessageStyle message={'Invalid Phone Number'} />
            )}

            <div className="flex text-xs mt-2 gap-4 justify-end">
              <button
                type="button"
                className="text-gray-700 font-semibold"
                onClick={(e) => {
                  setPhoneNumber('');
                  onPhoneNumberChange && onPhoneNumberChange('');
                  onPhoneNumberVerify && onPhoneNumberVerify(true);
                }}
              >
                Cancel
              </button>
              <button
                form="sendVerificationCodeForm"
                type="button"
                className={`${
                  isValidPhoneNumber
                    ? 'text-green cursor-pointer'
                    : 'text-gray-700 cursor-default'
                } font-semibold disabled:text-gray-700`}
                disabled={!isValidPhoneNumber}
                onClick={sendVerificationCodeHandle}
              >
                Verify
              </button>
            </div>
          </>
        )}
      </div>

      <VerifyCodeModal.Component>
        <OverlayWindow
          size={WindowSize.lg}
          onClose={VerifyCodeModal.forceClose}
          title={'Verify you new phone number'}
        >
          <div className="px-2 pb-4">
            <div className="text-sm text-gray-700">
              A verification code is sent to{' '}
              <span className="font-semibold">{phoneNumber}</span> please copy
              it below to verify this number
            </div>

            <div className="py-4">
              <Label label={'Verification Code'} />
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={styles.text_input}
                placeholder={'Enter code here'}
              />
            </div>

            <div className="border-t w-full my-4 border-gray-800"></div>
            <div className="text-sm py-4 flex justify-end gray-light-10">
              <div className="w-1/3 flex gap-2">
                <CustomButton
                  type="button"
                  text="Cancel"
                  bgColor="white"
                  textColor="gray-light"
                  borderColor="gray-800"
                  height="10"
                  textSize="text-sm"
                  onClick={VerifyCodeModal.forceClose}
                />

                <CustomButton
                  textSize="text-sm"
                  type="submit"
                  form="checkVerificationCodeForm"
                  text="Verify"
                  height="10"
                  disabled={!code}
                  onClick={(e) => {
                    checkVerificationCodeHandle();
                  }}
                />
              </div>
            </div>
          </div>
        </OverlayWindow>
      </VerifyCodeModal.Component>
    </>
  );
}
